import React, { useRef } from "react";
import useAddMadDocument from "../../hooks/useAddMadDocument";
import useAddNotification from "../../hooks/useAddNotification";
import { GET_MAD } from "../../hooks/useGetMad";
import useTranslations from "../../hooks/useTranslations";
import { PlusIcon } from "../icons";
import { Button } from "../wombat-ui";
import { DocumentPlate } from "../utils/DocumentPlate";
import useDeleteMadDocument from "../../hooks/useDeleteMadDocument";
import { MadFragment } from "../../graphql/fragments";

type UserFollowUpDocumentsProps = {
  mad: MadFragment;
};

const UserFollowUpDocuments: React.FC<UserFollowUpDocumentsProps> = ({
  mad,
}) => {
  const t = useTranslations();

  const fileUploadField = useRef();

  const [
    addMadDocument,
    { loading: addMadDocumentLoading },
  ] = useAddMadDocument({
    refetchQueries: [{ query: GET_MAD, variables: { id: mad.id } }],
  });
  const [addNotification] = useAddNotification();

  const [
    deleteMadDocument,
    { loading: deleteMadDocumentLoading },
  ] = useDeleteMadDocument({
    refetchQueries: [{ query: GET_MAD, variables: { id: mad.id } }],
  });

  const handleUploadDocument = (e) => {
    const reader = new FileReader();
    const file = e.target.files[0];
    reader.readAsDataURL(file);
    reader.onload = () => {
      addMadDocument({
        variables: {
          input: {
            id: mad.id,
            data: reader.result,
            filename: file.name,
          },
        },
      }).catch(() => {
        addNotification({
          variables: {
            title: t("forms.errors.notificationTitle") as string,
            body: t("forms.errors.invalidFile") as string,
            status: "error",
          },
        });
      });
    };
  };

  const addDocumentButton =  (
    <div className="flex justify-center pt-3">
      <Button
        variant='subtle'
        onClick={() => fileUploadField?.current?.click()}
        disabled={addMadDocumentLoading}
        id="uploadDocumentButton"
      >
        {t("mads.addDocument")}
      </Button>
    </div>
  );

  return (
    <div className="-mt-2 sm:col-span-2">
      <dt className="flex items-center justify-between text-sm font-medium text-light-900">
        <span>Documents</span>
        <div>
          <input
            ref={fileUploadField}
            type="file"
            onChange={handleUploadDocument}
            className="absolute inset-0 w-px h-px p-0 m-0 opacity-0"
            name="document"
          />
        </div>
      </dt>
      <dd className="text-sm text-dark-100">
        {mad.documents.length === 0 ? (
          <div className="flex justify-center py-2 text-sm text-dark-100">
            <div className="flex flex-col space-y-3">
              <p className="text-light-900">{t("mads.noDocuments")}</p>
              {
                mad.canAddDocument && addDocumentButton
              }
            </div>
          </div>
        ) : (
          <>
            <ul className="pl-3 pr-4 mt-1 border border-gray-200 divide-y divide-gray-200 rounded-md" id="documentPlates">
              {mad.documents.map((document) => (
                <DocumentPlate
                  allowDelete={mad.canDeleteDocument}
                  deleteDocument={deleteMadDocument}
                  deleteLoading={deleteMadDocumentLoading}
                  document={document}
                  key={document.id}
                />
              ))}
            </ul>
            {
              mad.canAddDocument && addDocumentButton
            }
          </>
        )}
      </dd>
    </div>
  );
};

export default UserFollowUpDocuments;
